import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";

export type TrainingSubgroupRef = {
  id: string;
  subgroup_code: string;
  label: string;
  member_count: number;
};

export type TrainingGroupRef = {
  id: string;
  group_no: number;
  label: string;
  subgroups: TrainingSubgroupRef[];
};

export type TrainingPeriodTree = {
  id: string;
  year: number;
  month: number;
  label: string;
  is_active: boolean;
  groups: TrainingGroupRef[];
};

export type StudentTrainingCohort = {
  period_id: string | null;
  group_id: string | null;
  subgroup_id: string;
  period_label: string;
  group_label: string | null;
  subgroup_label: string | null;
};

export function formatPeriodLabel(year: number, month: number): string {
  return `${year}\\${month}`;
}

export async function fetchTrainingCohortTree(
  includeInactive = false,
): Promise<TrainingPeriodTree[]> {
  return api.get<TrainingPeriodTree[]>("/training-cohorts/tree", {
    include_inactive: includeInactive ? "true" : undefined,
  });
}

export async function createTrainingPeriod(input: {
  year: number;
  month: number;
  groupCount?: number;
  subgroupsPerGroup?: number;
}): Promise<TrainingPeriodTree> {
  return api.post<TrainingPeriodTree>("/training-cohorts/periods", input);
}

export async function addTrainingGroup(periodId: string): Promise<TrainingGroupRef> {
  return api.post<TrainingGroupRef>(`/training-cohorts/periods/${periodId}/groups`, {});
}

export async function addTrainingSubgroup(groupId: string): Promise<TrainingSubgroupRef> {
  return api.post<TrainingSubgroupRef>(`/training-cohorts/groups/${groupId}/subgroups`, {});
}

export async function deleteTrainingGroup(groupId: string): Promise<void> {
  await api.delete(`/training-cohorts/groups/${groupId}`);
}

export async function deleteTrainingSubgroup(subgroupId: string): Promise<void> {
  await api.delete(`/training-cohorts/subgroups/${subgroupId}`);
}

export function useTrainingCohortTree(): {
  tree: TrainingPeriodTree[];
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [tree, setTree] = useState<TrainingPeriodTree[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchTrainingCohortTree()
      .then((data) => {
        if (!controller.signal.aborted) setTree(data);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Egitim donemleri yuklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  return { tree, loading, error, reload };
}
